import React, {
    Children,
    cloneElement,
    FC,
    isValidElement,
    memo,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import debounce from "lodash/debounce";

import {
    Animated,
    StyleProp,
    ViewStyle,
    ViewProps,
    ScrollView as RNScrollView,
    LayoutChangeEvent,
    NativeSyntheticEvent,
    NativeScrollEvent,
    StyleSheet,
} from 'react-native';
import type { TabItemProps } from './TabItem';

import { View, ScrollView} from "../native-base";
import { Divider } from "../Divider";
import useSafeCallback from "../../hooks/useSafeCallback";

import type TabItem from './TabItem';
import { useThemeColors } from '../hooks';
import { IDividerProps } from 'native-base';
import { Indicator } from './Indicator';

type TabItemType = React.ReactElement<React.ComponentProps<typeof TabItem>, typeof TabItem>;

export type TabsProps = ViewProps & {
    /**
     * child tab name
     * */
    value?: string;
    /**
     * defaultValue to preselected for uncontrolled mode
     * */
    defaultValue?: string;
    /**
     * to enable scroll
     * */
    scrollable?: boolean;
    /**
     * on name change callback.
     * */
    onChange?: (value: string) => void;
    /**
     * Disable the active indicator below.
     * */
    disableIndicator?: boolean;
    /**
     * Additional styling for tab indicator.
     * */
    indicatorStyle?: StyleProp<ViewStyle>;

    indicatorProps?: Omit<ViewStyle, 'style'>;

    dividerProps?: IDividerProps;

    /** Define the background Variant. */
    variant?: 'primary' | 'secondary';
    activeColor?: string;
    children: TabItemType | TabItemType[];
};


const noop = () => {};

const defaultIndicatorStyles=  {
    position: 'absolute',
    bottom: 0,
} as const;

const itemContainerStyles = {
    flexDirection: 'row',
    position: 'relative',
    flex: 1,
} as const

export const TabBase = ({
    children,
    value: valueProp,
    defaultValue,
    scrollable = false,
    onChange: onChangeProp = noop,
    indicatorStyle: indicatorStyleProp = defaultIndicatorStyles,
    disableIndicator,
    style,
    variant = 'primary',
    indicatorProps,
    dividerProps,
    activeColor,
    testID,
    ...rest
}: TabsProps) => {

    const {primaryColor,} = useThemeColors();

    activeColor = activeColor || primaryColor;

    const validChildren = useMemo(
        () =>
            Children.toArray(children).filter(
                child => isValidElement(child) && (child?.type as FC).displayName === 'Tabs_Item',
            ),
        [children],
    );

    const nameToIndexMap = useMemo(
        () =>
            validChildren.reduce((acc, child, currentIndex) => {
                acc[(child as ReactElement).props?.name] = currentIndex;
                return acc;
            }, {} as Record<string, number>),
        [validChildren],
    );

    const childPositionRef = useRef<Array<[string, number]>>([]);

    

    const isControlled = useRef(valueProp !== undefined).current;
    const [valueLocal, onChangeLocal] = useState(valueProp ?? defaultValue);

    const value = isControlled ? valueProp : valueLocal;
    const onChange = useSafeCallback<((value: string) => void)>((value) => {
        !isControlled && onChangeLocal(value);
        onChangeProp?.(value);
    });

    const valueIndex = nameToIndexMap[value];

    const scrollViewRef = useRef<RNScrollView>(null);
    const scrollViewPosition = useRef(0);

    const tabItemPositions = useRef<Array<{ width: number; contentWidth: number }>>([]);
    const [tabContainerWidth, setTabContainerWidth] = useState(0);
    const [force, forceUpdate] = useState(0);

    const itemPositionsMap = useMemo(() => {
        return tabItemPositions.current.reduce((acc, item, index) => {
            const previousItemsWidth = tabItemPositions.current
                .slice(0, index)
                .reduce((totalWidth, _item) => {
                    totalWidth += _item.width || 0;

                    return totalWidth;
                }, 0);

            acc[index] =
                variant === 'primary'
                    ? previousItemsWidth + (item.width - item.contentWidth) / 2
                    : previousItemsWidth;

            return acc;
        }, {} as Record<number, number>);
        // to make useMemo in sync with the ref
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant, tabItemPositions.current.length, force]);


    const itemWidthsMap = useMemo(() => {
        return tabItemPositions.current.reduce((acc, item, index) => {
            acc[index] = variant === 'primary' ? item.contentWidth : item.width;

            return acc;
        }, {} as Record<number, number>);
        // to make useMemo in sync with the ref
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant, tabItemPositions.current.length,  force]);


    const onScrollHandler = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
        scrollViewPosition.current = event.nativeEvent.contentOffset.x;
    }, []);

    // const transitionInterpolateWithMap = useCallback(
    //     (obj: Record<any, number>) => {
    //         const countItems = validChildren.length;
    //         if (countItems < 2 || !tabItemPositions.current.length) {
    //             // if there's only one item, use the value of that
    //             return Object.values(obj)[0] || 0;
    //         }
    //         const inputRange = Array.from(Array(countItems).keys());
    //         const outputValues = Object.values(obj);
    //         const outputRange = inputRange.map((_, index) => outputValues[0] ?? 0);

    //         return positionAnimationRef.current.interpolate({
    //             inputRange,
    //             outputRange,
    //         });
    //     },
    //     [validChildren.length],
    // );

    const indicatorPosition = useMemo(() => {
        return itemPositionsMap[valueIndex] || 0;
    }, [itemPositionsMap, valueIndex]);

    const indicatorWidth = useMemo(() => {
        return itemWidthsMap[valueIndex] || 0;
    }, [itemWidthsMap, valueIndex]);

    const Container = scrollable ? ScrollView : View;
    const containerProps = scrollable
        ? {
              horizontal: true,
              ref: scrollViewRef,
              onScroll: onScrollHandler,
              showsHorizontalScrollIndicator: false,
              style: itemContainerStyles,
          }
        : {
            style: itemContainerStyles,
        };


    const debounceUpdate = useMemo(() => {
        return debounce(() => {
            forceUpdate((prev) => prev + 1);
        }, 100, { trailing: true });
    }, []);

    const onLayout = useCallback(({ nativeEvent: { layout } }: LayoutChangeEvent) => {
        setTabContainerWidth(layout.width);
    }, []);

    const onLayoutItem = useCallback((event: LayoutChangeEvent, index: number) => {
        const { width } = event.nativeEvent.layout;

        const currentItemPosition = tabItemPositions.current[index];

        tabItemPositions.current[index] = {
            ...currentItemPosition,
            width: width,
        };

        debounceUpdate();
    }, []);

    const onLayoutText = useCallback((event: LayoutChangeEvent, index: number) => {
        const { width } = event.nativeEvent.layout;

        const currentItemPosition = tabItemPositions.current[index];

        tabItemPositions.current[index] = {
            ...currentItemPosition,
            contentWidth: width,
        };

        debounceUpdate();
    }, []);

    



    const indicatorStyles = useMemo(() => StyleSheet.flatten([
        defaultIndicatorStyles,
        indicatorStyleProp,
        {
            backgroundColor: activeColor,
            height: 4,
            transform: `translateX(${indicatorPosition}px)`,
            width: indicatorWidth,
        },
    ]), [indicatorWidth, indicatorPosition, activeColor]);


    const scrollHandler = useCallback(
        (currValue: number) => {
            if (tabItemPositions.current.length > currValue) {
                const currentTab = tabItemPositions.current[currValue];
                const itemStartPosition = currValue === 0 ? 0 : itemPositionsMap[currValue - 1];
                const itemEndPosition = itemPositionsMap[currValue];

                const scrollCurrentPosition = scrollViewPosition.current;
                const tabContainerCurrentWidth = tabContainerWidth;

                let scrollX = scrollCurrentPosition;

                if (itemStartPosition < scrollCurrentPosition) {
                    scrollX += itemStartPosition - scrollCurrentPosition;
                } else if (scrollCurrentPosition + tabContainerCurrentWidth < itemEndPosition) {
                    scrollX += itemEndPosition - (scrollCurrentPosition + tabContainerCurrentWidth) + currentTab.width;
                }

                scrollViewRef.current?.scrollTo({
                    x: scrollX,
                    y: 0,
                    animated: true,
                });
            }
        },
        [itemPositionsMap, tabContainerWidth],
    );



    const hasItemPositions = Object.values(itemPositionsMap).length;
    const shouldHideIndicator = disableIndicator || !hasItemPositions;

    useEffect(() => {
        if (!hasItemPositions) return;
        scrollable && requestAnimationFrame(() => scrollHandler(valueIndex));
    }, [hasItemPositions, scrollHandler, valueIndex, scrollable]);

    // useEffect(() => {
    //     Animated.timing(widthAnimationRef.current, {
    //         toValue: valueIndex,
    //         useNativeDriver: false,
    //         duration: 170,
    //     }).start();
    // }, [positionAnimationRef, scrollHandler, valueIndex]);

    useEffect(() => {
        const shouldUpdate = childPositionRef.current.length !== validChildren.length;
        childPositionRef.current = validChildren.map((child, index) => [(child as ReactElement).props.name, index]);

        if (shouldUpdate) forceUpdate((prev) => prev + 1);
    }, [validChildren]);

    const handleRemove = useCallback((key) => {
        const index = childPositionRef.current.find(([itemKey]) => itemKey === key)?.[1];
        if (index < 0) return;

        tabItemPositions.current.splice(index, 1);
    }, []);

    return (
        <View
            {...rest}
            testID={testID}
            accessibilityRole="tablist"
            onLayout={onLayout}>
            <>
                <Container
                    testID={testID && `${testID}--inner-container`}
                    {...containerProps}>
                    {validChildren.map((child, index) => (
                        <ChildItem
                            key={(child as ReactElement).props.name}
                            recordKey={(child as ReactElement).props.name }
                            testID={testID && `${testID}--tab-item`}
                            index={index}
                            value={value}
                            child={child as ReactElement<TabItemProps>}
                            onChange={onChange}
                            onLayout={onLayoutItem}
                            onLayoutContent={onLayoutText}
                            variant={variant}
                            onRemove={handleRemove}
                            disabled={(child as ReactElement).props.disabled}
                        />
                    ))}

                    {!shouldHideIndicator && (
                        <Indicator
                            data-test-id={testID && `${testID}--active-indicator`}
                            {...indicatorProps}
                            style={indicatorStyles as {}}
                        />
                    )}
                </Container>
                
                <Divider
                    orientation='horizontal'
                    testID={testID && `${testID}--divider`}
                    bg="muted.300"
                    mb="3"
                    {...dividerProps}
                />
                
                {(validChildren.find((child) => (child as ReactElement).props.name === value) as ReactElement)?.props.render?.()}
            </>
        </View>
    );
};

type ChildItemProps = {
    variant: TabsProps['variant'];
    child: ReactElement<TabItemProps>;
    onChange: TabsProps['onChange'];
    onLayout: (event: LayoutChangeEvent, index: number) => void;
    onLayoutContent: (event: LayoutChangeEvent, index: number) => void;
    value: string;
    index: number;
    testID?: string;
    recordKey: string;
    onRemove: (key: string) => void
    disabled?: boolean;
};

const ChildItem = memo(
    ({
        value,
        child,
        onChange,
        onLayout: onLayoutProp,
        onLayoutContent: onLayoutContentProp,
        onRemove,
        recordKey,
        variant,
        index,
        testID,
        disabled,
    }: ChildItemProps) => {
        const name = child.props?.name;

        const onPress = useCallback(() => {
            if (disabled) return;
            onChange?.(name);
        }, [disabled, name, onChange]);

        const onLayout = useCallback(
            (e: LayoutChangeEvent) => {
                onLayoutProp(e, index);
            },
            [index, onLayoutProp],
        );

        const onLayoutContent = useCallback(
            (e: LayoutChangeEvent) => {
                onLayoutContentProp(e, index);
            },
            [index, onLayoutContentProp],
        );

        useEffect(() => {
            return () => {
                onRemove(recordKey);
            }
        }, [onRemove, recordKey]);

        return cloneElement(child, {
            onPress,
            onLayout,
            active: name === value,
            onLayoutContent,
            variant,
            testID: child.props.testID ?? testID,
        });
    },
);

TabBase.displayName = 'Tabs';
